/* eslint-disable react-hooks/exhaustive-deps */

import React, { memo, useCallback, useEffect, useState } from "react";

import {
  AddressManualEntryField,
  AddressResult,
  AddressLookup as ODSAddressLookup,
  QasProfile,
  qasLookup,
} from "address-lookup";
import { useFormContext, useFormState } from "react-hook-form";

import { Stack } from "@origin-digital/ods-core";
import { task } from "@origin-digital/reporting-client";

import {
  abortPreviousRequestsIfAny,
  getServiceabilityAddressUrlKey,
} from "@/abort";
import { RouterActions, StyledHeading } from "@/components";
import { config } from "@/config";
import { ANALYTICS_FORM_NAME, ANALYTICS_TASK_TYPE_MAP } from "@/constants";
import {
  combineManualEntryToAddress,
  formatAddressDetails,
} from "@/features/Address/";
import { isBatteryJourney } from "@/features/SolarForm/utils/formJourney";
import { useFieldValidation } from "@/hooks";
import { useRedirect } from "@/hooks/useRedirect";

import { address as content } from "./content.json";
import { useServiceability } from "./useServiceability";

const defaultManualEntryValues = {
  unitType: undefined,
  unitNumber: undefined,
  streetNumber: undefined,
  streetName: undefined,
  city: undefined,
  state: undefined,
  postcode: undefined,
  streetType: "",
};

export const Address = memo(
  ({
    backRoute,
    nextRoute,
    hardStopRoute,
    batteryNextRoute,
    selectedSystemType: systemType,
  }: {
    backRoute: () => Promise<string>;
    nextRoute: () => Promise<string>;
    hardStopRoute: () => Promise<string>;
    batteryNextRoute: () => Promise<string>;
    selectedSystemType: string;
  }) => {
    const { setValue, getValues } = useFormContext();

    const { isValid } = useFieldValidation({
      fields: ["addressDetails"],
    });
    let isSystemTypeValid = false;

    const { checkAddressAndSystem } = useServiceability();
    const { goNextRoute } = useRedirect();

    const {
      errors: { addressDetails: error },
    } = useFormState({ name: "addressDetails" });
    const [showManualEntryFields, setShowManualEntryFields] = useState(false);
    const [lookupAddress, setLookupAddress] = useState(
      getValues("address") || undefined
    );
    const [helperText, setHelperText] = useState<string>("");

    const isAddressManualEntryFields = getValues("isAddressManualEntryFields");
    const addressDetails = getValues("addressDetails");

    const [manualEntryValues, setManualEntryValues] = useState(() =>
      isAddressManualEntryFields ? addressDetails : defaultManualEntryValues
    );

    const [address, setAddress] = useState(() =>
      isAddressManualEntryFields ? "" : getValues("address")
    );
    //Making these values to false when ever user navigate back to address page
    setValue("degradedExperience", false);

    const { isValid: systemValidation } = useFieldValidation({
      fields: ["systemType"],
    });

    const validateManualEntryFields = () => {
      document.getElementById("address-manual-entry-streetNumber")?.focus();
      document.getElementById("address-manual-entry-streetNumber")?.blur();
      document.getElementById("address-manual-entry-streetName")?.focus();
      document.getElementById("address-manual-entry-streetName")?.blur();
      document.getElementById("address-manual-entry-city")?.focus();
      document.getElementById("address-manual-entry-city")?.blur();
      document.getElementById("address-manual-entry-state")?.focus();
      document.getElementById("address-manual-entry-state")?.blur();
      document.getElementById("address-manual-entry-postcode")?.focus();
      document.getElementById("address-manual-entry-postcode")?.blur();

      if (manualEntryValues.unitType) {
        document.getElementById("address-manual-entry-unitNumber")?.focus();
        document.getElementById("address-manual-entry-unitNumber")?.blur();
      }
    };

    const changeHelperText = () => {
      let autocompleteMessage: string = "";

      if (!address) {
        autocompleteMessage = content.message.required;
      } else if (!lookupAddress) {
        autocompleteMessage = content.message.invalidAddress;
      } else if (error) {
        autocompleteMessage = content.message.invalidAddress;
      }

      if (autocompleteMessage) {
        setHelperText(autocompleteMessage);
      }
    };

    const validate = () => {
      if (showManualEntryFields) {
        validateManualEntryFields();
      }

      changeHelperText();

      return isValid();
    };

    const doNext = async () => {
      isSystemTypeValid = await systemValidation();
      const valid = await validate();

      const redirectToHardStopRoute = async () => {
        setValue("hardStop", "address");
        const to = await hardStopRoute();
        goNextRoute(to);
      };

      if (valid) {
        const result = await checkAddressAndSystem(
          formatAddressDetails(getValues("addressDetails")),
          systemType
        );

        if (isSystemTypeValid) {
          task.base(ANALYTICS_TASK_TYPE_MAP[systemType], {
            action: "start",
            formName: ANALYTICS_FORM_NAME,
            addressState: addressDetails.state,
          });
        }

        if (result.error) {
          await redirectToHardStopRoute();
          return false;
        }

        setValue(
          "address",
          combineManualEntryToAddress(getValues("addressDetails"))
        );

        return true;
      }

      return valid;
    };

    const { fetchAddresses, fetchAddressDetails } = qasLookup(
      config.addressApiProps.qasAuthToken,
      QasProfile.AUS
    );

    const handleManualEntryShown = () => {
      // Manual Entry Fields is shown
      // clean up fields value to signal the input is changed and ready to re-validate later
      setShowManualEntryFields(true);
      setValue("isAddressManualEntryFields", true);
      setAddress("");
      setManualEntryValues("");
      setValue("address", "");
      setValue("addressDetails", "");

      return Promise.resolve();
    };

    useEffect(() => {
      if (isAddressManualEntryFields) {
        setShowManualEntryFields(true);
      }

      // clean up
      return () => {
        abortPreviousRequestsIfAny(getServiceabilityAddressUrlKey());
      };
    }, []);

    const handleSelect = useCallback(async (addressResult: AddressResult) => {
      setShowManualEntryFields(false);
      setValue("isAddressManualEntryFields", false);
      setHelperText("");

      try {
        const qasAddress = await fetchAddressDetails(addressResult.addressId);

        setLookupAddress(qasAddress);
        setAddress(addressResult.label);
        setValue("address", addressResult.label);

        // eslint-disable-next-line @typescript-eslint/no-shadow
        const addressDetails = {
          unitType: {
            label: "",
            value: qasAddress.unitType,
          },
          unitNumber: qasAddress.unitNumber,
          streetNumber: qasAddress.streetNumber,
          streetName: qasAddress.streetName,
          city: qasAddress.city,
          state: qasAddress.state,
          postcode: qasAddress.postcode,
          streetType: qasAddress.streetType,
        };

        addressDetails["streetName"] = addressDetails["streetName"]
          .replaceAll(addressDetails["streetType"] || "", "")
          .trim();

        setValue("addressDetails", addressDetails);

        await isValid();
      } catch (e) {
        setHelperText(content.message.failedRequest);
      }
    }, []);

    const handleQueryChange = useCallback((query: string) => {
      setHelperText("");

      setAddress(query);

      // handleQueryChange is called when
      // - the X button is clicked
      // - address input is changed (add / remove character)
      // clean up fields value to signal the input is changed and ready to re-validate later
      setLookupAddress(undefined);
      setValue("address", "");
      setValue("addressDetails", "");
      setShowManualEntryFields(false);
      setValue("isAddressManualEntryFields", false);
    }, []);

    const handleManualEntryChange = useCallback(
      (fields: AddressManualEntryField, value: string) => {
        const updatedManualEntryFields = { ...manualEntryValues };

        updatedManualEntryFields[fields] = value;
        updatedManualEntryFields["streetType"] = "";

        setManualEntryValues(updatedManualEntryFields);
        setValue("addressDetails", updatedManualEntryFields);
      },
      [manualEntryValues]
    );

    const handleNextRoute = useCallback(() => {
      return isBatteryJourney(systemType) ? batteryNextRoute() : nextRoute();
    }, [systemType]);

    return (
      <>
        <Stack space="medium">
          <StyledHeading>{content.heading}</StyledHeading>
          <ODSAddressLookup
            id={content.id}
            fetchAddresses={fetchAddresses}
            onSelect={handleSelect}
            onQueryChange={handleQueryChange}
            onBlur={changeHelperText}
            value={address}
            manualEntryValues={manualEntryValues}
            manualEntry
            manualEntryProps={{
              showAddressButton: false,
              id: "",
              onSubmit: () => {},
              onChange: handleManualEntryChange,
              manualEntryValues,
            }}
            onManualEntryShown={handleManualEntryShown}
            error={!!helperText && !showManualEntryFields}
            helperText={
              helperText && !showManualEntryFields
                ? helperText
                : content.description
            }
          />
        </Stack>
        <RouterActions
          onBackRoute={backRoute}
          onNextRoute={handleNextRoute}
          onNext={doNext}
        />
      </>
    );
  }
);
